import type {
  ProfileModuleDto,
  ProfileTemplateFieldPoliciesDto,
} from '@up2stay/up2promo-api-client';
import {
  ProfileHeaderStyle,
  ProfileLayout,
  ProfileTheme,
} from '@up2stay/up2promo-api-client';

import { normalizeFieldPolicies } from '@/core/lib/profile-template-fields';
import type { TemplateFormData } from '@/core/lib/profile-template-form';
import { sanitizeModulesForProfileApi } from '@/core/lib/sanitize-profile-modules-for-api';

/** Stable form shape for snapshot comparison and API-bound content. */
export function normalizeTemplateFormForSnapshot(
  form: TemplateFormData,
): TemplateFormData {
  const trimOrNull = (value: string | null | undefined) => {
    const trimmed = (value ?? '').trim();
    return trimmed ? trimmed : null;
  };

  return {
    jobTitle: trimOrNull(form.jobTitle),
    company: trimOrNull(form.company),
    location: trimOrNull(form.location),
    showProfileImage: form.showProfileImage ?? true,
    showUp2promoBranding: form.showUp2promoBranding ?? true,
    accentColor: form.accentColor ?? null,
    layout: form.layout ?? ProfileLayout.CARDS,
    theme: form.theme ?? ProfileTheme.AUTO,
    headerStyle: form.headerStyle ?? ProfileHeaderStyle.GRADIENT,
    headerImage: form.headerImage?.id
      ? { id: form.headerImage.id, path: form.headerImage.path }
      : null,
    buttonShape: form.buttonShape ?? null,
    language: form.language ?? 'auto',
    visibility: form.visibility ?? 'unlisted',
    leadFormEnabled: form.leadFormEnabled ?? false,
    leadFormVisibilityMode: form.leadFormVisibilityMode ?? 'manual',
    leadFormAutoOpenDelay:
      typeof form.leadFormAutoOpenDelay === 'number'
        ? form.leadFormAutoOpenDelay
        : 0,
    leadsAutoShareWithWorkspace: form.leadsAutoShareWithWorkspace ?? false,
  };
}

export function sanitizeModulesForTemplateApi(
  modules: ProfileModuleDto[],
): ProfileModuleDto[] {
  return sanitizeModulesForProfileApi(modules).map((module) => {
    if (module.type === 'team_members' && module.config) {
      return { ...module, config: { ...module.config, members: [] } };
    }
    return module;
  });
}

export function buildTemplateEditorSnapshot(params: {
  name: string;
  description: string;
  policies: ProfileTemplateFieldPoliciesDto;
  form: TemplateFormData;
  modules: ProfileModuleDto[];
  teamIds?: string[];
}): string {
  return JSON.stringify({
    name: params.name.trim(),
    description: params.description.trim(),
    policies: normalizeFieldPolicies(params.policies),
    form: normalizeTemplateFormForSnapshot(params.form),
    modules: sanitizeModulesForTemplateApi(params.modules),
    teamIds: [...(params.teamIds ?? [])].sort(),
  });
}

export function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: unknown } }).response;
    const data = response?.data;
    if (data && typeof data === 'object') {
      const errors = (data as { errors?: Record<string, unknown> }).errors;
      if (errors && typeof errors === 'object') {
        const parts: string[] = [];
        const collect = (value: unknown, prefix = '') => {
          if (typeof value === 'string' && value.trim()) {
            parts.push(prefix ? `${prefix}: ${value}` : value);
            return;
          }
          if (value && typeof value === 'object') {
            for (const [key, nested] of Object.entries(
              value as Record<string, unknown>,
            )) {
              collect(nested, prefix ? `${prefix}.${key}` : key);
            }
          }
        };
        collect(errors);
        if (parts.length > 0) {
          return parts.slice(0, 3).join(' · ');
        }
      }
      const message = (data as { message?: string }).message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  }
  if (err instanceof Error && err.message.trim()) {
    return err.message;
  }
  return fallback;
}
